import React, { useState } from 'react';
import { Check, FileText, FileStack, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';

const PACKAGES = [
  {
    id: 'BASIS',
    name: 'Basis',
    price: 1500,
    docs: 0,
    icon: FileText,
    description: 'Rask vurdering basert på din beskrivelse',
    includes: [
      'AI-assistert juridisk vurdering',
      'Konkrete anbefalinger',
      'Neste steg beskrevet',
      'Leveringstid: 24-48 timer'
    ],
    popular: false
  },
  {
    id: 'DOCUMENT',
    name: 'Dokument',
    price: 2500,
    docs: 1,
    icon: FileStack,
    description: 'Gjennomgang av 1 dokument + vurdering',
    includes: [
      'Alt fra Basis-pakken',
      'Gjennomgang av 1 dokument',
      'Dokumentanalyse og risikovurdering',
      'Detaljert skriftlig vurdering',
      'Leveringstid: 48-72 timer'
    ],
    popular: true
  },
  {
    id: 'EXTENDED',
    name: 'Utvidet',
    price: 4500,
    docs: 5,
    icon: Sparkles,
    description: 'Helhetlig gjennomgang av flere dokumenter',
    includes: [
      'Alt fra Dokument-pakken',
      'Gjennomgang av opptil 5 dokumenter',
      'Omfattende juridisk analyse',
      'Handlingsplan og strategi',
      'Leveringstid: 3-5 virkedager'
    ],
    popular: false
  }
];

const PackageSelector = ({ onSelect }) => {
  const [selectedPackage, setSelectedPackage] = useState(null);

  const handleSelect = (pkg) => {
    setSelectedPackage(pkg.id);
  };

  const handleConfirm = () => {
    const pkg = PACKAGES.find(p => p.id === selectedPackage);
    if (pkg && onSelect) {
      onSelect(pkg);
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Velg din pakke
        </h2>
        <p className="text-gray-600">
          Alle pakker har fastpris og inkluderer juridisk vurdering fra godkjent advokat
        </p>
      </div>

      {/* Package Cards */}
      <div className="grid md:grid-cols-3 gap-6 mb-6">
        {PACKAGES.map((pkg) => {
          const Icon = pkg.icon;
          const isSelected = selectedPackage === pkg.id;
          
          return (
            <button
              key={pkg.id}
              onClick={() => handleSelect(pkg)}
              className={`relative p-6 rounded-xl border-2 transition-all text-left ${
                isSelected
                  ? 'border-gray-900 bg-gray-50 shadow-lg'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
              }`}
            >
              {/* Popular Badge */}
              {pkg.popular && (
                <div className="absolute -top-3 left-1/2 -trangray-x-1/2">
                  <span className="bg-gray-900 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Mest valgt
                  </span>
                </div>
              )}

              {/* Icon */}
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
                isSelected ? 'bg-gray-900' : 'bg-gray-100'
              }`}>
                <Icon className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-gray-600'}`} />
              </div>

              {/* Package Name */}
              <h3 className="text-xl font-bold text-gray-900 mb-1">{pkg.name}</h3>
              
              {/* Price */}
              <div className="mb-3">
                <span className="text-3xl font-bold text-gray-900">
                  {pkg.price.toLocaleString('nb-NO')} kr
                </span>
              </div>

              {/* Description */}
              <p className="text-sm text-gray-600 mb-4">
                {pkg.description}
              </p>

              {/* Includes */}
              <ul className="space-y-2 mb-4">
                {pkg.includes.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                    <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              {/* Documents */}
              {pkg.docs > 0 && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs font-medium text-gray-600">
                    📎 Inkluderer {pkg.docs} dokument{pkg.docs > 1 ? 'er' : ''}
                  </p>
                </div>
              )}

              {/* Selected Indicator */}
              {isSelected && (
                <div className="absolute top-4 right-4">
                  <div className="w-6 h-6 bg-gray-900 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Confirm Button */}
      <div className="text-center">
        <Button
          onClick={handleConfirm}
          disabled={!selectedPackage}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 text-lg"
        >
          Fortsett med {selectedPackage && PACKAGES.find(p => p.id === selectedPackage)?.name}-pakken
        </Button>
      </div>

      {/* Trust Signal */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
          <Check className="w-4 h-4 text-green-600" />
          <span>Betalingen reserveres. Du betaler kun når advokaten leverer svar.</span>
        </div>
      </div>
    </div>
  );
};

export default PackageSelector;
export { PACKAGES };
